import { useMemo } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { html } from '@codemirror/lang-html'
import { javascript } from '@codemirror/lang-javascript'
import { oneDark } from '@codemirror/theme-one-dark'
import { autocompletion, type CompletionSource } from '@codemirror/autocomplete'
import { EditorView } from '@codemirror/view'

import { useTheme } from '../contexts/ThemeContext'
import {
  generalVariableCompletions,
  layoutTemplateCompletions,
  thirdPartyCompletions,
} from '../lib/adCodeVariables'

/**
 * Which placeholder vocabulary the editor completes.
 * - `layoutTemplate`: every Handlebars variable, for layout render templates.
 * - `generalVariables`: the variables substituted into any creative code component.
 * - `thirdPartyCode`: the general variables plus the click/cachebuster placeholders
 *   Adnuntius recognises in third-party ad tags.
 */
export type CodeEditorVariables =
  | 'layoutTemplate'
  | 'generalVariables'
  | 'thirdPartyCode'
  | 'none'

interface CodeEditorProps {
  value: string
  onChange: (value: string) => void
  /** Syntax highlighting language. Defaults to HTML (which also highlights inline JS). */
  language?: 'html' | 'javascript'
  /** Placeholder autocomplete to enable. Defaults to layout render template variables. */
  variables?: CodeEditorVariables
  readOnly?: boolean
  /**
   * Read-only display rather than a disabled editor: the content is not
   * contenteditable and the editor's own cursor, active-line and selection
   * drawing are off, so the browser's native text selection is used. Dragging
   * to select inside a `readOnly` editor fights with CodeMirror's own pointer
   * handling, which makes selecting a tag to copy unreliable.
   */
  display?: boolean
  minHeight?: string
  maxHeight?: string
  placeholder?: string
  className?: string
}

const COMPLETION_SOURCES: Record<CodeEditorVariables, CompletionSource[]> = {
  layoutTemplate: [layoutTemplateCompletions],
  generalVariables: [generalVariableCompletions],
  thirdPartyCode: [generalVariableCompletions, thirdPartyCompletions],
  none: [],
}

export function CodeEditor({
  value,
  onChange,
  language = 'html',
  variables = 'layoutTemplate',
  readOnly = false,
  display = false,
  minHeight = '200px',
  maxHeight,
  placeholder,
  className,
}: CodeEditorProps) {
  const { theme } = useTheme()

  const extensions = useMemo(() => {
    const sources = COMPLETION_SOURCES[variables]
    return [
      language === 'javascript' ? javascript() : html(),
      EditorView.lineWrapping,
      // minHeight on its own only grows the editor box — the editable area and the
      // line-number gutter need it too, so the whole box is clickable and filled.
      EditorView.theme({
        // The radius has to match the wrapper's, or the wrapper clips the
        // square corners off the editor's own border.
        '&': { minHeight, borderRadius: 'var(--ad-radius-sm)', ...(maxHeight ? { maxHeight } : {}) },
        '.cm-scroller': { overflow: 'auto' },
        '.cm-content, .cm-gutter': { minHeight },
      }),
      // The stock light theme is pure white; blend it into the card like an Input does.
      ...(theme === 'dark'
        ? []
        : [
            EditorView.theme({
              '&.cm-editor': {
                backgroundColor: 'transparent',
                border: '1px solid var(--input)',
              },
              '&.cm-editor .cm-gutters': {
                backgroundColor: 'transparent',
                borderRight: '1px solid var(--border)',
              },
              '&.cm-editor .cm-activeLine, &.cm-editor .cm-activeLineGutter': {
                backgroundColor: 'var(--ad-neutral-soft)',
              },
            }),
          ]),
      ...(sources.length > 0 ? [autocompletion({ override: sources })] : []),
    ]
  }, [language, variables, minHeight, maxHeight, theme])

  // Everything a display block has no use for, and whose pointer handling is
  // what makes selection awkward.
  const basicSetup = display
    ? {
        lineNumbers: false,
        foldGutter: false,
        highlightActiveLine: false,
        highlightActiveLineGutter: false,
        drawSelection: false,
        dropCursor: false,
        rectangularSelection: false,
        crosshairCursor: false,
        allowMultipleSelections: false,
        highlightSelectionMatches: false,
        autocompletion: false,
        history: false,
      }
    : undefined

  return (
    <div className={className}>
      <CodeMirror
        value={value}
        onChange={onChange}
        extensions={extensions}
        theme={theme === 'dark' ? oneDark : 'light'}
        readOnly={readOnly}
        editable={!display}
        {...(basicSetup ? { basicSetup } : {})}
        minHeight={minHeight}
        maxHeight={maxHeight}
        placeholder={placeholder}
        style={{ fontSize: 13, borderRadius: 'var(--ad-radius-sm)', overflow: 'hidden' }}
      />
    </div>
  )
}
